import { appColors } from 'constants/colors';
import { CloseCircleFilled } from '@ant-design/icons';

import { CheckIconXs, WarningIconMd } from '@chownow/pantry/icons';

export const INTEGRATION_STATUS = {
  published: {
    label: 'Published',
    color: appColors.publishMenuPublished,
  },
  modified: {
    label: 'Modified',
    color: appColors.publishMenuModified,
  },
  in_progress: {
    label: 'Publishing',
    color: appColors.publishMenuInProgress,
  },
  failed: {
    label: 'Failed',
    color: appColors.publishMenuFailed,
  },
  pulled: {
    label: 'Imported',
    color: appColors.publishMenuImported,
  },
  inactive: {
    label: 'Inactive',
    color: appColors.publishMenuInactive,
  },
};

export const MENU_SOURCE_TAG_COLOURS = {
  pos: '#A4C2D8',
  app: '#A1ECBB',
  default: '#D1ADDFF',
  text: '#16173E',
  clone: '#D4F182',
};

export const LOADING_NUM_ROWS = 40;

export const MENU_DUPLICATION_SUCCESSFUL = 'MENU_DUPLICATION_SUCCESSFUL';
export const MENU_DUPLICATION_FAILED = 'MENU_DUPLICATION_FAILED';
export const MENU_IMPORT_SUCCESSFUL = 'MENU_IMPORT_SUCCESSFUL';
export const MENU_IMPORT_FAILED = 'MENU_IMPORT_FAILED';
export const MENU_BUILD_FILE_UPLOADED = 'MENU_BUILD_FILE_UPLOADED';
export const MENU_BUILD_FILE_PROCESSED = 'MENU_BUILD_FILE_PROCESSED';
export const MENU_BUILD_MENU_CREATED = 'MENU_BUILD_MENU_CREATED';
export const MENU_BUILD_MENU_INSERTED = 'MENU_BUILD_MENU_INSERTED';
export const MENU_BUILD_MENU_FAILED = 'MENU_BUILD_MENU_FAILED';

export const CUBOH_INTEGRATION = 'cuboh';
export const CUBOH_MENUS = 'cubohMenus';
export const SINGLE_MENU = 'singleMenu';

export const MENU_OBJECT_TYPES = {
  single: 'single_menu',
  menus: 'cuboh_menu',
  categories: 'cuboh_category',
  items: 'cuboh_main_item',
  modifierGroups: 'cuboh_modifier_group',
  modifiers: 'cuboh_modifier',
};

export const MENU_TYPES = {
  STANDARD: 'standard',
  CATERING: 'catering',
};

export const MENU_TYPE_LABELS = {
  [MENU_TYPES.STANDARD]: 'Takeout',
  [MENU_TYPES.CATERING]: 'Catering',
};

/** An unset type is takeout per the BE, so it resolves to standard rather than its own bucket.
 * @param {string} [menuType] - Raw menuType off the menu, which imports may leave unset
 * @returns {string} - A MENU_TYPES value */
export const resolveMenuType = menuType =>
  (menuType === MENU_TYPES.CATERING ? MENU_TYPES.CATERING : MENU_TYPES.STANDARD);

export const CUBOH_MENU_CHILD_MAP = {
  [MENU_OBJECT_TYPES.menus]: MENU_OBJECT_TYPES.categories,
  [MENU_OBJECT_TYPES.categories]: MENU_OBJECT_TYPES.items,
  [MENU_OBJECT_TYPES.items]: MENU_OBJECT_TYPES.modifierGroups,
  [MENU_OBJECT_TYPES.modifierGroups]: MENU_OBJECT_TYPES.modifiers,
  [MENU_OBJECT_TYPES.modifiers]: MENU_OBJECT_TYPES.modifierGroups,
};

export const ITEM_TYPES_FOR_PARENT_CHILD = [
  MENU_OBJECT_TYPES.categories,
  MENU_OBJECT_TYPES.items,
  MENU_OBJECT_TYPES.modifierGroups,
  MENU_OBJECT_TYPES.modifiers,
];

/** Action Types */
export const SET_CUBOH_MENU_ITEMS = 'SET_CUBOH_MENU_ITEMS';
export const SET_SINGLE_MENU = 'SET_SINGLE_MENU';
export const SET_POS_MENUS = 'SET_POS_MENUS';
export const SET_IS_LOADING = 'SET_IS_LOADING';
export const SET_ERROR = 'SET_ERROR';
export const UPDATE_CUBOH_MENU_ITEM = 'UPDATE_CUBOH_MENU_ITEM';
export const SET_DEFAULT_STATE = 'SET_DEFAULT_STATE';
export const CREATE_NEW_CUBOH_ITEM = 'CREATE_NEW_CUBOH_ITEM';
export const UPDATE_MENU_INTEGRATION = 'UPDATE_MENU_INTEGRATION';
export const SET_ALL_PARENT_CHILD_OPTIONS = 'SET_ALL_PARENT_CHILD_OPTIONS';
export const SET_PARENT_CHILD_IS_LOADING = 'SET_PARENT_CHILD_IS_LOADING';
export const SET_PARENT_CHILD_ERROR = 'SET_PARENT_CHILD_ERROR';
export const SET_MERCHANT_INTEGRATIONS = 'SET_MERCHANT_INTEGRATIONS';
export const SET_INTEGRATIONS_IS_LOADING = 'SET_INTEGRATIONS_IS_LOADING';
export const SET_INTEGRATIONS_ERROR = 'SET_MERCHANT_INTEGRATIONS_ERROR';
export const SET_ALL_SUPPORTED_INTEGRATIONS = 'SET_ALL_SUPPORTED_INTEGRATIONS';
export const SET_SUPPORTED_IS_LOADING = 'SET_SUPPORTED_IS_LOADING';
export const SET_SUPPORTED_ERROR = 'SET_SUPPORTED_ERROR';
export const SET_AVAILABLE_CLASSIFICATIONS = 'SET_AVAILABLE_CLASSIFICATIONS';
export const SET_CLASSIFICATIONS_IS_LOADING = 'SET_CLASSIFICATIONS_IS_LOADING';
export const SET_CLASSIFICATIONS_ERROR = 'SET_CLASSIFICATIONS_ERROR';
export const AUTO_MAPPING_COMPLETE = 'AUTO_MAPPING_COMPLETE';
export const MENU_FIXER_COMPLETE = 'MENU_FIXER_COMPLETE';

export const TYPE_TO_READABLE_STRING_MAP = {
  cuboh_menu: 'Menu',
  cuboh_category: 'Category',
  cuboh_main_item: 'Item',
  cuboh_modifier_group: 'Modifier Group',
  cuboh_modifier: 'Modifier',
};
export const TYPE_TO_READABLE_STRING_PLURAL = {
  cuboh_menu: 'Menus',
  cuboh_category: 'Categories',
  cuboh_main_item: 'Items',
  cuboh_modifier_group: 'Modifier Groups',
  cuboh_modifier: 'Modifiers',
};

export const MENU_CACHE_READY = 'MENU_CACHE_READY';
export const MENU_CACHE_FAILED = 'MENU_CACHE_FAILED';

export const MENU_INFO = { value: 'menu-info', label: 'Menu Info' };
export const MENU_ITEMS = { value: 'menu-items', label: 'Menu Items' };

export const PUBLISH_NOTICE =
  'The menus have been successfully published to the external integration queue and may, depending on the platform, take up to 90 minutes to go live.';

export const PUBLISH_ERROR_MESSAGE =
  'We had an issue publishing your results. Please contact customer support.';

export const PUBLISH_FAILURE_REASONS = {
  500: 'There was an error while building your menus. Please refresh the page and try again. If the problem persists, please contact support for assistance.',
  400: 'There was an issue with your request. Please refresh the page and try again. If the problem persists, please contact support for assistance.',
};

export const SUPPORTS_DISABLED = ['ChowNow', 'Cuboh'];

export const CUBOH_MENU_PARENT_MAP = {
  [MENU_OBJECT_TYPES.categories]: [MENU_OBJECT_TYPES.menus],
  [MENU_OBJECT_TYPES.items]: [MENU_OBJECT_TYPES.categories],
  [MENU_OBJECT_TYPES.modifierGroups]: [MENU_OBJECT_TYPES.items, MENU_OBJECT_TYPES.modifiers],
  [MENU_OBJECT_TYPES.modifiers]: [MENU_OBJECT_TYPES.modifierGroups],
};

export const PUBLISH_LOCKED_TOOLTIP =
  'Publishing is not allowed yet, the Cuboh team is still preparing this account.';

export const PUBLISH_ALL_TOOLTIP = "Publish all this merchant's menus to all integrations.";

export const ALL_MENUS_PUBLISHED_TOOLTIP = 'This menu is up-to-date on all integrations.';

export const NO_INTEGRATIONS_TOOLTIP = 'This menu has no linked integrations to publish to.';

export const DISABLED_DUE_TO_LINKED_MERCHANTS_TOOLTIP_TITLE =
  'Please save changes to linked merchants to continue editing your menu.';

export const SPECIAL_HOURS_TOOLTIP =
  'Specify Special/Holiday hours which apply to particular days of the year. These will override the regular hours for the specified days and will automatically be included in any menu publishes within 6 days of the specified hours.';

export const PUBLISH_TOOLTIP = 'Publish Menus';

export const PUBLISH_MENU_VALIDATION_SUCCESSFUL = 'PUBLISH_MENU_VALIDATION_SUCCESSFUL';
export const PUBLISH_MENU_VALIDATION_FAILED = 'PUBLISH_MENU_VALIDATION_FAILED';
export const PUBLISH_BUILD_SUCCESSFUL = 'PUBLISH_BUILD_SUCCESSFUL';
export const PUBLISH_BUILD_FAILED = 'PUBLISH_BUILD_FAILED';

export const CHOWNOW_RESULT_ICON = {
  pass: { Icon: CheckIconXs, stroke: '#0e7a4a', type: 'success' },
  warning: { Icon: WarningIconMd, stroke: '#b2afa4', type: 'neutral' },
  fail: { Icon: CloseCircleFilled, stroke: '#cc1b00', type: 'error' },
};

export const OLDEST_POSSIBLE_PUBLISH_DATE_IN_MILLISECONDS = 1546329600000;

export const INTEGRATION_STATUS_CHANGED = 'INTEGRATION_STATUS_CHANGED';

export const CLONED_MENU_SOURCE = 'Cloned Menu';

export const CUBOH_MENU_SOURCE = 'Cuboh';

export const GENERATED_MENU_SOURCE = 'Generated';

export const MENU_UPLOAD_FEEDBACK_URL =
  'https://app.ballparkhq.com/share/self-guided/TzEXUHkFPCPXhaxzLzgMqU';

export const DAYS_AHEAD_TO_PUBLISH_SCHEDULE = 6;
export const START_INDEX = 0;
export const END_INDEX = 1;
export const DEFAULT_SCHEDULE = {
  dayIndex: 'sunday',
  startTime: '09:00',
  endTime: '17:00',
};
export const MIDNIGHT = '00:00';
export const OPEN_24_HOURS = 'Open 24 Hours';
export const ALWAYS_OPEN_TIME = '23:59';

export const LATEST_ROLLOVER_TIME = '06:00';

export const SCHEDULE_DAY_OPTIONS = [
  { value: 'monday', label: 'Mon', ordinal: 1 },
  { value: 'tuesday', label: 'Tue', ordinal: 2 },
  { value: 'wednesday', label: 'Wed', ordinal: 3 },
  { value: 'thursday', label: 'Thu', ordinal: 4 },
  { value: 'friday', label: 'Fri', ordinal: 5 },
  { value: 'saturday', label: 'Sat', ordinal: 6 },
  { value: 'sunday', label: 'Sun', ordinal: 7 },
];

export const DAY_SORTER = SCHEDULE_DAY_OPTIONS.reduce((accumulator, current) => {
  accumulator[current.value] = current.ordinal;
  return accumulator;
}, {});

export const MENU_NAME_MAX_LENGTH = 100;
export const MODIFIER_MINIMUM = 0;
export const MODIFIER_MAXIMUM = 99;

export const NON_REQUIRED_FIELDS = [
  'description',
  'imageUrl',
  'modifierGroup',
  'externalId',
  'specialInstructionsEnabled',
  'internalName',
  'servingsTag',
];

export const DEFAULT_IMAGE_FILE_NAME = 'default-menu-item-image.png';

export const ERROR_IMAGE_FILE_NAME = 'invalid-menu-item-image.png';

export const SPECIAL_INSTRUCTIONS = {
  key: 'specialInstructionsEnabled',
  apps: {
    ritual: 'Ritual',
    ubereats: 'UberEats',
  },
};

export const SECONDARY_PRICES = {
  key: 'secondaryPrice',
  apps: {
    doordash: 'DoorDash',
  },
};

export const SKIP_ONLY_TAXES = [
  'ALCOHOL',
  'NO_TAX',
  'SUGARED_DRINK',
  'BAKED_GOODS',
  'NON_FOOD',
  'GST_ONLY',
  'PST_ONLY',
];

export const MENU_MANAGEMENT_FIELDS = {
  DESCRIPTION: 'description',
  IMAGE_URL: 'imageUrl',
  MODIFIER_GROUP: 'modifierGroup',
  EXTERNAL_ID: 'externalId',
  SPECIAL_INSTRUCTIONS_ENABLED: 'specialInstructionsEnabled',
  INTERNAL_NAME: 'internalName',
};

export const TYPED_TIME_FORMATS = [
  'hh:mm a',
  'HH:mm',
  'HHmm',
  'Hmm',
  'ha',
  'hhmma',
  'hmma',
  'hh mm a',
  'hhmm a',
  'h mm a',
  'h a',
  'HH',
  'H',
  'hh mma',
  'hh:mma',
  'h:mma',
  'h:mm a',
];

export const CHOWNOW_TYPE_TO_READABLE_STRING_MAP = {
  cuboh_menu: 'Menu',
  cuboh_category: 'Category',
  cuboh_main_item: 'Item',
  cuboh_modifier_group: 'Mod Group',
  cuboh_modifier: 'Modifier',
};

export const FIELD_INPUT_TYPES = {
  NUMBER: 'number',
  PARENT: 'parent',
  PRICE: 'price',
  IMAGE: 'photo',
  UPLOAD: 'upload',
  CLASSIFICATIONS: 'classifications',
  CHECKBOX: 'checkbox',
  DROPDOWN_SELECTOR: 'dropdownSelector',
  MULTIPLE_MODIFIERS_CHECKBOX: 'allowMultipleModifiersCheckbox',
};

export const ALCOHOLIC = 'alcoholic';
export const ALCOHOLIC_NOT_AVAILABLE_ALONE = 'alcoholic_not_available_alone';
export const ALCOHOLIC_AVAILABLE_ALONE = 'alcoholic_available_alone';

export const SKIP_CLASSIFICATIONS = {
  ALCOHOL: {
    label: 'Skip Tax: Alcohol',
    alone: true,
    classification: 'ALCOHOL',
    skipTax: true,
  },
  NO_TAX: {
    label: 'Skip Tax: No Tax',
    alone: true,
    classification: 'NO_TAX',
    skipTax: true,
  },
  SUGARED_DRINK: {
    label: 'Skip Tax: Sugared Drink',
    alone: true,
    classification: 'SUGARED_DRINK',
    skipTax: true,
  },
  BAKED_GOODS: {
    label: 'Skip Tax: Baked Goods',
    alone: true,
    classification: 'BAKED_GOODS',
    skipTax: true,
  },
  NON_FOOD: {
    label: 'Skip Tax: Non Food',
    alone: true,
    classification: 'NON_FOOD',
    skipTax: true,
  },
  GST_ONLY: {
    label: 'Skip Tax: GST Only',
    alone: true,
    classification: 'GST_ONLY',
    skipTax: true,
  },
  PST_ONLY: {
    label: 'Skip Tax: PST Only',
    alone: true,
    classification: 'PST_ONLY',
    skipTax: true,
  },
};

/** Derives a long label from a classification key (e.g. 'dairy_free' → 'Dairy Free'). */
export const classificationFallbackLabel = key =>
  key?.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase()) || '';

/** Derives a short label from a classification key (e.g. 'dairy_free' → 'DF'). */
export const classificationFallbackShortLabel = key =>
  key
    ?.split('_')
    .map(w => w[0].toUpperCase())
    .join('') || '';

/** Builds classifications purely from backend data.
 * @param {Array} backendClassifications - Array of classification objects
 * @returns {Object} - Classifications keyed by itemClass (or alcoholic variant) */
export const buildAvailableClassifications = (backendClassifications = []) => {
  if (!backendClassifications?.length) {
    return {};
  }

  const built = {};

  backendClassifications.forEach(item => {
    const { itemClass, availableOrderAlone, classification, longLabel, shortLabel } = item;

    let key;
    if (classification === ALCOHOLIC) {
      key = availableOrderAlone ? ALCOHOLIC_AVAILABLE_ALONE : ALCOHOLIC_NOT_AVAILABLE_ALONE;
    } else {
      key = itemClass;
    }

    built[key] = {
      label: longLabel || classificationFallbackLabel(key),
      shortLabel: shortLabel || classificationFallbackShortLabel(key),
      alone: availableOrderAlone,
      classification,
    };
  });

  return built;
};

/** Combines regular classifications with Skip Tax ones from SKIP_CLASSIFICATIONS.
 * @param {Array} backendClassifications - Backend classifications array
 * @returns {Object} - Combined regular and Skip Tax classifications */
export const buildSkipClassifications = (backendClassifications = []) => {
  const regularClassifications = buildAvailableClassifications(backendClassifications);

  return {
    ...regularClassifications,
    ...SKIP_CLASSIFICATIONS,
  };
};

export const TAX_SELECT_OPTIONS = {
  DEFAULT: 'default',
  CUSTOM: 'custom',
};

export const MENU_MANAGEMENT_TOOLTIP_WIDTH = 320;

export const EMPTY_FIELD = '-';

export const DAY_TO_WEEKDAY = {
  Mon: 'monday',
  Tue: 'tuesday',
  Wed: 'wednesday',
  Thu: 'thursday',
  Fri: 'friday',
  Sat: 'saturday',
  Sun: 'sunday',
};

export const DAYS_OF_THE_WEEK = {
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
  SUNDAY: 'sunday',
};

export const MENU_NOTIFICATION_EVENT = 'menu-notification';

export const MENU_NOTIFICATION_TYPE = {
  SUCCESS: 'success',
  ERROR: 'error',
};

export const CAMEL_TYPES = {
  menus: 'cubohMenu',
  categories: 'cubohCategory',
  items: 'cubohMainItem',
  modifierGroups: 'cubohModifierGroup',
  modifiers: 'cubohModifier',
};

export const TAB_VALUES = {
  MENU_INFO: 'details',
  MENU_ITEMS: 'items',
  MENU_MAPPING: 'mapping',
  MENU_PHOTOS: 'photos',
};

export const SQUARE_APP_ID = 'squareapp';

export const VIEW_MODE = {
  INPUT: 'input',
  TIMELINE: 'timeline',
};

export const TIMELINE_COLORS = {
  PICKUP: 'var(--surface-paper-3)',
  DELIVERY: 'var(--surface-paper-4)',
  HOLIDAY: 'var(--surface-paper-5)',
};

export const SAVE_ERROR_MESSAGE = 'We could not save your menu. Please contact customer support.';
